import {
  validateDateComparisonRequired,
  validateSisDateForCustomSearch,
  validateYmdListAndStartEndNotDuplicated,
} from '../../utils/apiParamsValidation';
import { ChainStoreReportsSettingDateRangeParams } from '../chainStoreReportsSettingDateRangeParams';
import { ComparisonGraphParams } from '../comparisonGraph';
import { DataChainStoreKiParams } from '../dataChainStoreKi';
import {
  DataChainStoreShuParams,
  DataChainStoreShuSummaryParams,
} from '../dataChainStoreShu';
import { DataChainStoreShuGraphParams } from '../dataChainStoreShuGraph';
import { DataKadoGraphParams } from '../dataKadoGraph';
import { ApiValidationError } from '../error';
import { CHAIN_STORE_API_WEEKDAY_HOLIDAY_COMPARATIVE_SECTION } from './date';

/**
 * ================================================
 * チェーン店レポート画面のAPIリクエスト前のパラメータバリデーション
 * ================================================
 */

type ChainStoreTableParams = DataChainStoreKiParams | DataChainStoreShuParams;

/**
 * 平日/土日祝比較（範囲選択）の新形式クエリが BE 併用 400 にならないことを確認する。
 * comparativeSection=weekdayHoliday のときは startDate/endDate のみ必須で、
 * ymd 列挙・比較日付との同時送信は禁止。比較期間必須チェックは適用しない。
 */
export const validateChainStoreWeekdayHolidayQuery = (
  params: ChainStoreTableParams
): void => {
  if (
    params.comparativeSection !==
    CHAIN_STORE_API_WEEKDAY_HOLIDAY_COMPARATIVE_SECTION
  ) {
    return;
  }

  if (!params.startDate || !params.endDate) {
    throw new ApiValidationError(
      'comparativeSection=weekdayHoliday のときは startDate と endDate が必須です',
      'comparativeSection',
      'WEEKDAY_HOLIDAY_START_END_REQUIRED'
    );
  }

  const hasYmdList =
    (params.ymdList?.length ?? 0) > 0 ||
    (params.ymdComparisonList?.length ?? 0) > 0;
  const hasComparisonDates =
    params.startComparisonDate !== undefined ||
    params.endComparisonDate !== undefined;

  if (hasYmdList || hasComparisonDates) {
    throw new ApiValidationError(
      'comparativeSection と ymdList / ymdComparisonList / startComparisonDate / endComparisonDate は同時に使用できません',
      'comparativeSection',
      'WEEKDAY_HOLIDAY_DATE_LIST_CONFLICT'
    );
  }
};

const validateChainStoreTableDateParams = (
  params: ChainStoreTableParams
): void => {
  // weekdayHoliday の併用禁止・必須チェックを優先（専用エラーコードを返すため）
  validateChainStoreWeekdayHolidayQuery(params);

  // 日付指定の重複チェック
  validateYmdListAndStartEndNotDuplicated(params);

  // 通常の日付フィールドのバリデーション（weekdayHoliday では比較期間必須チェックを適用しない）
  if (
    params.comparativeSection !==
    CHAIN_STORE_API_WEEKDAY_HOLIDAY_COMPARATIVE_SECTION
  ) {
    validateDateComparisonRequired(params);
  }
};

/**
 * APIリクエスト前のパラメータバリデーション
 * チェーン店レポート 新台/メイン機種のテーブルのデータを取得する
 * yield call前に呼び出すことで、不正なパラメータでのAPIリクエストを防ぐ
 *
 * @param params チェック対象のパラメータオブジェクト
 * @param dateUnit 検索タイプ（ReportsDateUnit）。カスタム検索タイプの判定に使用
 */
export const validateChainStoreKiParams = (
  params: DataChainStoreKiParams,
  dateUnit?: ChainStoreReportsSettingDateRangeParams['dateUnit']
): void => {
  validateChainStoreTableDateParams(params);

  // DK-SISのカスタム検索タイプ時のバリデーション
  // カスタム検索タイプ（範囲選択・自由選択）の場合、sisStartDate/sisEndDateはSIS_CUSTOM_SEARCH_DUMMY_DATEである必要がある
  validateSisDateForCustomSearch(params, dateUnit);
};

/**
 * APIリクエスト前のパラメータバリデーション
 * チェーン店レポート 種別実績テーブルを取得する
 * yield call前に呼び出すことで、不正なパラメータでのAPIリクエストを防ぐ
 */
export const validateChainStoreShuParams = (
  params: DataChainStoreShuParams
): void => {
  validateChainStoreTableDateParams(params);
};

/**
 * APIリクエスト前のパラメータバリデーション
 * チェーン店レポート 店舗全体実績を取得する
 * yield call前に呼び出すことで、不正なパラメータでのAPIリクエストを防ぐ
 */
export const validateChainStoreWholeParams = (
  params: DataChainStoreShuParams
): void => {
  validateChainStoreTableDateParams(params);
};

/**
 * APIリクエスト前のパラメータバリデーション
 * チェーン店レポート 種別実績サマリーを取得する
 * yield call前に呼び出すことで、不正なパラメータでのAPIリクエストを防ぐ
 */
export const validateChainStoreSummaryParams = (
  params: DataChainStoreShuSummaryParams
): void => {
  // 日付指定の重複チェック
  validateYmdListAndStartEndNotDuplicated(params);
};

/**
 * APIリクエスト前のパラメータバリデーション
 * チェーン店レポート 店舗比較グラフを取得する
 * yield call前に呼び出すことで、不正なパラメータでのAPIリクエストを防ぐ
 */
export const validateChainStoreShuGraphParams = (
  params: DataChainStoreShuGraphParams
): void => {
  // 日付指定の重複チェック
  validateYmdListAndStartEndNotDuplicated(params);
};

/**
 * APIリクエスト前のパラメータバリデーション
 * チェーン店レポート 機種比較グラフを取得する
 * yield call前に呼び出すことで、不正なパラメータでのAPIリクエストを防ぐ
 */
export const validateChainStoreKiGraphParams = (
  params: ComparisonGraphParams
): void => {
  // 日付指定の重複チェック
  validateYmdListAndStartEndNotDuplicated(params);
};

/**
 * APIリクエスト前のパラメータバリデーション
 * チェーン店レポート 時間帯別稼働数グラフを取得する
 * yield call前に呼び出すことで、不正なパラメータでのAPIリクエストを防ぐ
 */
export const validateChainStoreShuKadoGraphParams = (
  params: DataKadoGraphParams
): void => {
  // 日付指定の重複チェック
  validateYmdListAndStartEndNotDuplicated(params);
  // 通常の日付フィールドのバリデーション
  validateDateComparisonRequired(params);
};
